import { Bedrag, Knop } from '@qc/ui';
import type { Evenement } from '@qc/db';
import { useApparaten } from './useApparaten.js';

export interface ApparatenProps {
  evenement: Evenement;
}

const STATUSTEKST: Record<string, string> = {
  op_voorraad: 'op voorraad',
  uitgegeven: 'uitgegeven',
  retour: 'terug',
  defect: 'defect',
};

/**
 * Welk toestel ligt bij welke kraam, en hoeveel dagen is het verhuurd.
 *
 * Die dagen zijn geen administratie: ze bepalen wat er aan kraamsets wordt
 * gefactureerd. En een toestel dat na afloop nog buiten staat is geld.
 */
export function Apparaten({ evenement }: ApparatenProps) {
  const beeld = useApparaten(evenement);

  if (beeld.laden) return <p className="text-tekst-zacht">Bezig met laden…</p>;

  if (beeld.fout !== null) {
    return (
      <div className="grid gap-4">
        <p className="text-lg text-fout">{beeld.fout}</p>
        <Knop nadruk="gewoon" onClick={() => void beeld.ververs()} className="w-fit">
          Nog eens proberen
        </Knop>
      </div>
    );
  }

  return (
    <div className="grid gap-6">
      <div className="grid gap-3 sm:grid-cols-4">
        {[
          { label: 'Totaal', waarde: String(beeld.overzicht.totaal) },
          { label: 'Uitgegeven', waarde: String(beeld.overzicht.uitgegeven) },
          { label: 'Op voorraad', waarde: String(beeld.overzicht.opVoorraad) },
          { label: 'Defect', waarde: String(beeld.overzicht.defect) },
        ].map((tegel) => (
          <div key={tegel.label} className="rounded-tegel border border-rand bg-vlak p-5">
            <p className="text-tekst-zacht">{tegel.label}</p>
            <p className="mt-1 text-3xl font-bold">{tegel.waarde}</p>
          </div>
        ))}
      </div>

      {beeld.overzicht.nietIngeleverd.length > 0 && (
        <p className="rounded-tegel border border-waarschuwing bg-vlak p-4 text-waarschuwing">
          Het evenement is afgesloten en er staan nog {beeld.overzicht.nietIngeleverd.length}{' '}
          toestellen buiten, samen <Bedrag centen={beeld.overzicht.buitenstaandeWaardeCent} /> aan
          vervangingswaarde.
        </p>
      )}

      <div className="overflow-x-auto rounded-tegel border border-rand bg-vlak">
        <table className="w-full min-w-[46rem] text-left">
          <thead className="border-b border-rand text-tekst-zacht">
            <tr>
              <th className="p-4 font-normal">Toestel</th>
              <th className="p-4 font-normal">Bij</th>
              <th className="p-4 text-right font-normal">Huurdagen</th>
              <th className="p-4 text-right font-normal">Waarde</th>
              <th className="p-4 font-normal">Actie</th>
            </tr>
          </thead>
          <tbody>
            {beeld.apparaten.map((apparaat) => (
              <tr key={apparaat.id} className="border-b border-rand last:border-0">
                <td className="p-4">
                  <span className="block">{apparaat.soort}</span>
                  <span className="block text-base text-tekst-zacht">
                    {apparaat.serienummer ?? 'geen serienummer'} ·{' '}
                    {STATUSTEKST[apparaat.status] ?? apparaat.status}
                  </span>
                </td>
                <td className="p-4">{apparaat.kraamnaam ?? '—'}</td>
                <td className="p-4 text-right tabular-nums">{apparaat.huurdagen}</td>
                <td className="p-4 text-right text-tekst-zacht">
                  <Bedrag centen={apparaat.vervangingswaardeCent} />
                </td>
                <td className="p-4">
                  {apparaat.status === 'uitgegeven' ? (
                    <Knop
                      nadruk="gewoon"
                      className="px-4 text-base"
                      onClick={() => void beeld.neemTerug(apparaat.id)}
                    >
                      Terug
                    </Knop>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      <select
                        className="min-h-12 rounded-tegel border border-rand bg-vlak-hoog px-3 text-base text-tekst"
                        defaultValue=""
                        onChange={(gebeurtenis) => {
                          const deelname = gebeurtenis.target.value;
                          if (deelname !== '') void beeld.geefUit(apparaat.id, deelname);
                        }}
                      >
                        <option value="">Uitgeven aan…</option>
                        {beeld.kramen.map((kraam) => (
                          <option key={kraam.id} value={kraam.id}>
                            {kraam.naam}
                          </option>
                        ))}
                      </select>
                      <Knop
                        nadruk={apparaat.status === 'defect' ? 'hoofd' : 'stil'}
                        className="px-4 text-base"
                        onClick={() =>
                          void beeld.zetDefect(apparaat.id, apparaat.status !== 'defect')
                        }
                      >
                        {apparaat.status === 'defect' ? 'Weer goed' : 'Defect'}
                      </Knop>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {beeld.apparaten.length === 0 && (
              <tr>
                <td colSpan={5} className="p-4 text-tekst-zacht">
                  Er staan nog geen toestellen in het systeem.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
